/**
 * Enterprise Trust Center Module Component
 * Customer-Facing Trust Portal Management
 */

import React, { useState, useEffect } from 'react';
import { useI18n } from '../../context/I18nContext';
import { trustCenterService } from '../../services/enterpriseService';
import { grcService } from '../../services/grcService';
import { Modal } from './SharedModals';
import {
  Shield, FileText, Download, Users, Eye, Lock, CheckCircle, Clock,
  RefreshCw, Plus, Settings, Search, ExternalLink, Calendar, Globe,
  BadgeCheck, Building2, BarChart2, Award, Mail, AlertTriangle, Link
} from 'lucide-react';

interface TrustCenterConfig {
  id: number;
  organization_id: number;
  portal_enabled: boolean;
  custom_domain?: string;
  logo_url?: string;
  primary_color?: string;
  visible_sections: string[];
  require_nda: boolean;
  auto_approve_domains: string[];
}

interface TrustDocument {
  id: number;
  title: string;
  document_type: 'soc2' | 'iso27001' | 'hipaa' | 'gdpr' | 'pentest' | 'questionnaire' | 'policy' | 'certificate' | 'other';
  description?: string;
  file_path?: string;
  is_public: boolean;
  requires_nda: boolean;
  download_count: number;
  expires_at?: string;
  created_at: string;
}

interface AccessRequest {
  id: number;
  requester_email: string;
  requester_name?: string;
  company_name?: string;
  documents_requested: number[];
  status: 'pending' | 'approved' | 'denied' | 'expired';
  nda_signed: boolean;
  created_at: string;
  resolved_at?: string;
}

interface PortalAnalytics {
  total_visitors: number;
  total_downloads: number;
  pending_requests: number;
  active_access: number;
  top_documents: { title: string; downloads: number }[];
}

export const EnterpriseTrustCenter: React.FC = () => {
  const { language } = useI18n();
  const [config, setConfig] = useState<TrustCenterConfig | null>(null);
  const [documents, setDocuments] = useState<TrustDocument[]>([]);
  const [requests, setRequests] = useState<AccessRequest[]>([]);
  const [analytics, setAnalytics] = useState<PortalAnalytics | null>(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState<'overview' | 'documents' | 'requests' | 'settings'>('overview');
  const [searchQuery, setSearchQuery] = useState('');

  // Modal states for policy import
  const [showImportPoliciesModal, setShowImportPoliciesModal] = useState(false);
  const [showAddDocumentModal, setShowAddDocumentModal] = useState(false);
  const [policies, setPolicies] = useState<any[]>([]);
  const [selectedPolicies, setSelectedPolicies] = useState<number[]>([]);
  const [importing, setImporting] = useState(false);
  const [savingConfig, setSavingConfig] = useState(false);

  // Settings form state
  const [settingsForm, setSettingsForm] = useState({
    custom_domain: '',
    primary_color: '#14b8a6',
    require_nda: false,
    auto_approve_domains: '',
    visible_sections: ['certifications', 'policies', 'sub-processors', 'questionnaire'] as string[]
  });

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    setLoading(true);
    try {
      const [configData, docsData, requestsData, analyticsData] = await Promise.all([
        trustCenterService.getConfig().catch(() => null),
        trustCenterService.getDocuments().catch(() => ({ documents: [] })),
        trustCenterService.getAccessRequests().catch(() => ({ requests: [] })),
        trustCenterService.getAnalytics().catch(() => null)
      ]);
      
      setConfig(configData?.data || configData);
      // Handle API response format
      const documentsList = docsData?.data || docsData?.documents || (Array.isArray(docsData) ? docsData : []);
      const requestsList = requestsData?.data || requestsData?.requests || (Array.isArray(requestsData) ? requestsData : []);
      setDocuments(documentsList);
      setRequests(requestsList);
      setAnalytics(analyticsData?.data || analyticsData);
      
      // Populate settings form from config
      if (configData) {
        setSettingsForm({
          custom_domain: configData.custom_domain || '',
          primary_color: configData.primary_color || '#14b8a6',
          require_nda: configData.require_nda || false,
          auto_approve_domains: (configData.auto_approve_domains || []).join(', '),
          visible_sections: configData.visible_sections || ['certifications', 'policies', 'sub-processors', 'questionnaire']
        });
      }
    } catch (error) {
      console.error('Error loading trust center data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleApproveRequest = async (requestId: number) => {
    try {
      await trustCenterService.approveAccessRequest(requestId);
    } catch (error) {
      console.error('Error approving request:', error);
    }
    loadData();
  };

  const handleDenyRequest = async (requestId: number) => {
    try {
      await trustCenterService.denyAccessRequest(requestId);
    } catch (error) {
      console.error('Error denying request:', error);
    }
    loadData();
  };

  const handleTogglePortal = async () => {
    const newEnabled = !config?.portal_enabled;
    try {
      await trustCenterService.updateConfig({ portal_enabled: newEnabled });
      setConfig(prev => prev ? { ...prev, portal_enabled: newEnabled } : prev);
    } catch (error) {
      console.error('Error toggling portal:', error);
      // Still update locally for UX
      setConfig(prev => prev ? { ...prev, portal_enabled: newEnabled } : prev);
    }
  };

  const handleSaveSettings = async () => {
    setSavingConfig(true);
    try {
      await trustCenterService.updateConfig({
        custom_domain: settingsForm.custom_domain || null,
        primary_color: settingsForm.primary_color,
        require_nda: settingsForm.require_nda,
        auto_approve_domains: settingsForm.auto_approve_domains.split(',').map(d => d.trim()).filter(Boolean),
        visible_sections: settingsForm.visible_sections
      });
      loadData();
    } catch (error) {
      console.error('Error saving settings:', error);
    } finally {
      setSavingConfig(false);
    }
  };

  const loadPolicies = async () => {
    try {
      // Get documents from GRC module (policies are stored there)
      const data = await grcService.getDocuments();
      const policyList = Array.isArray(data) ? data : ((data as any)?.documents || []);
      setPolicies(policyList);
    } catch (error) {
      console.error('Error loading policies:', error);
    }
  };

  const handleImportPolicies = async () => {
    if (selectedPolicies.length === 0) return;
    setImporting(true);
    try {
      // Import selected policies as trust center documents
      for (const policyId of selectedPolicies) {
        const policy = policies.find(p => p.id === policyId);
        if (policy) {
          await trustCenterService.addDocument({
            title: policy.title || policy.name,
            document_type: 'policy',
            description: policy.description || '',
            is_public: false,
            requires_nda: false
          });
        }
      }
      setShowImportPoliciesModal(false);
      setSelectedPolicies([]);
      loadData();
    } catch (error) {
      console.error('Error importing policies:', error);
    } finally {
      setImporting(false);
    }
  };

  const openImportPoliciesModal = () => {
    loadPolicies();
    setShowImportPoliciesModal(true);
  };

  const documentTypes = {
    soc2: { label: 'SOC 2', color: 'blue' },
    iso27001: { label: 'ISO 27001', color: 'green' },
    hipaa: { label: 'HIPAA', color: 'purple' },
    gdpr: { label: 'GDPR', color: 'indigo' },
    pentest: { label: language === 'sr' ? 'Pentest' : 'Pentest', color: 'red' },
    questionnaire: { label: language === 'sr' ? 'Upitnik' : 'Questionnaire', color: 'yellow' },
    policy: { label: language === 'sr' ? 'Politika' : 'Policy', color: 'gray' },
    certificate: { label: language === 'sr' ? 'Certifikat' : 'Certificate', color: 'emerald' },
    other: { label: language === 'sr' ? 'Ostalo' : 'Other', color: 'gray' }
  };

  const getTypeColor = (type: string) => {
    const colors: Record<string, string> = {
      blue: 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-300',
      green: 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-300',
      purple: 'bg-purple-100 text-purple-700 dark:bg-purple-900/30 dark:text-purple-300',
      indigo: 'bg-indigo-100 text-indigo-700 dark:bg-indigo-900/30 dark:text-indigo-300',
      red: 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-300',
      yellow: 'bg-yellow-100 text-yellow-700 dark:bg-yellow-900/30 dark:text-yellow-300',
      emerald: 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-300',
      gray: 'bg-gray-100 text-gray-700 dark:bg-gray-700 dark:text-gray-300'
    };
    const typeInfo = documentTypes[type as keyof typeof documentTypes];
    return colors[typeInfo?.color || 'gray'];
  };

  const pendingRequests = requests.filter(r => r.status === 'pending');

  const filteredDocuments = documents.filter(doc =>
    doc.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
    doc.description?.toLowerCase().includes(searchQuery.toLowerCase())
  );

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <RefreshCw className="w-8 h-8 animate-spin text-indigo-500" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold text-gray-900 dark:text-white flex items-center gap-3">
            <BadgeCheck className="w-7 h-7 text-teal-500" />
            {language === 'sr' ? 'Trust Center' : 'Trust Center'}
          </h2>
          <p className="text-gray-500 dark:text-gray-400 mt-1">
            {language === 'sr' 
              ? 'Upravljajte portalom povjerenja za vaše klijente'
              : 'Manage your customer-facing trust portal'
            }
          </p>
        </div>
        <div className="flex items-center gap-3">
          <button className="flex items-center gap-2 px-4 py-2 border border-gray-200 dark:border-gray-700 rounded-lg text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700">
            <ExternalLink className="w-4 h-4" />
            {language === 'sr' ? 'Otvori portal' : 'Open Portal'}
          </button>
          <button 
            onClick={openImportPoliciesModal}
            className="flex items-center gap-2 px-4 py-2 border border-teal-600 text-teal-600 rounded-lg hover:bg-teal-50 dark:hover:bg-teal-900/20"
          >
            <Link className="w-4 h-4" />
            {language === 'sr' ? 'Uvezi politike' : 'Import Policies'}
          </button>
          <button 
            onClick={() => setShowAddDocumentModal(true)}
            className="flex items-center gap-2 px-4 py-2 bg-teal-600 text-white rounded-lg hover:bg-teal-700 transition-colors"
          >
            <Plus className="w-4 h-4" />
            {language === 'sr' ? 'Dodaj dokument' : 'Add Document'}
          </button>
        </div>
      </div>

      {/* Portal Status Banner */}
      <div className={`rounded-xl p-4 flex items-center justify-between ${
        config?.portal_enabled 
          ? 'bg-teal-50 dark:bg-teal-900/20 border border-teal-200 dark:border-teal-800'
          : 'bg-gray-50 dark:bg-gray-800 border border-gray-200 dark:border-gray-700'
      }`}>
        <div className="flex items-center gap-3">
          <Globe className={`w-6 h-6 ${config?.portal_enabled ? 'text-teal-500' : 'text-gray-400'}`} />
          <div>
            <div className="font-medium text-gray-900 dark:text-white">
              {config?.portal_enabled 
                ? (language === 'sr' ? 'Portal je aktivan' : 'Portal is Live')
                : (language === 'sr' ? 'Portal je neaktivan' : 'Portal is Offline')
              }
            </div>
            {config?.custom_domain && (
              <div className="text-sm text-gray-500">{config.custom_domain}</div>
            )}
          </div>
        </div>
        <label className="relative inline-flex items-center cursor-pointer">
          <input type="checkbox" checked={config?.portal_enabled || false} className="sr-only peer" onChange={handleTogglePortal} />
          <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none rounded-full peer dark:bg-gray-700 peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all dark:border-gray-600 peer-checked:bg-teal-600"></div>
        </label>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-5">
          <div className="flex items-center justify-between mb-3">
            <FileText className="w-8 h-8 text-teal-500" />
          </div>
          <div className="text-3xl font-bold text-gray-900 dark:text-white">{documents.length}</div>
          <div className="text-sm text-gray-500 dark:text-gray-400">{language === 'sr' ? 'Dokumenata' : 'Documents'}</div>
        </div>
        
        <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-5">
          <div className="flex items-center justify-between mb-3">
            <Download className="w-8 h-8 text-blue-500" />
          </div>
          <div className="text-3xl font-bold text-gray-900 dark:text-white">{analytics?.total_downloads || 0}</div>
          <div className="text-sm text-gray-500 dark:text-gray-400">{language === 'sr' ? 'Preuzimanja' : 'Downloads'}</div>
        </div>
        
        <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-5">
          <div className="flex items-center justify-between mb-3">
            <Clock className="w-8 h-8 text-orange-500" />
            {pendingRequests.length > 0 && (
              <span className="px-2 py-0.5 bg-orange-100 text-orange-700 dark:bg-orange-900/30 dark:text-orange-300 rounded-full text-xs">
                {language === 'sr' ? 'Novo' : 'New'}
              </span>
            )}
          </div>
          <div className="text-3xl font-bold text-gray-900 dark:text-white">{pendingRequests.length}</div>
          <div className="text-sm text-gray-500 dark:text-gray-400">{language === 'sr' ? 'Na čekanju' : 'Pending'}</div>
        </div>
        
        <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-5">
          <div className="flex items-center justify-between mb-3">
            <Users className="w-8 h-8 text-indigo-500" />
          </div>
          <div className="text-3xl font-bold text-gray-900 dark:text-white">{analytics?.active_access || 0}</div>
          <div className="text-sm text-gray-500 dark:text-gray-400">{language === 'sr' ? 'Aktivni pristup' : 'Active Access'}</div>
        </div>
      </div>

      {/* Tabs */}
      <div className="flex gap-2 border-b border-gray-200 dark:border-gray-700">
        {[
          { key: 'overview', icon: BarChart2, label: language === 'sr' ? 'Pregled' : 'Overview' },
          { key: 'documents', icon: FileText, label: language === 'sr' ? 'Dokumenti' : 'Documents' },
          { key: 'requests', icon: Users, label: language === 'sr' ? 'Zahtjevi' : 'Requests', badge: pendingRequests.length },
          { key: 'settings', icon: Settings, label: language === 'sr' ? 'Postavke' : 'Settings' }
        ].map(tab => (
          <button
            key={tab.key}
            onClick={() => setActiveTab(tab.key as typeof activeTab)}
            className={`flex items-center gap-2 px-4 py-3 border-b-2 transition-colors ${
              activeTab === tab.key
                ? 'border-teal-500 text-teal-600 dark:text-teal-400'
                : 'border-transparent text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200'
            }`}
          >
            <tab.icon className="w-4 h-4" />
            {tab.label}
            {tab.badge ? (
              <span className="ml-1 px-2 py-0.5 bg-orange-100 text-orange-700 dark:bg-orange-900/30 dark:text-orange-300 rounded-full text-xs">
                {tab.badge}
              </span>
            ) : null}
          </button>
        ))}
      </div>

      {/* Overview Tab */}
      {activeTab === 'overview' && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Certifications */}
          <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-5">
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4 flex items-center gap-2">
              <Award className="w-5 h-5 text-emerald-500" />
              {language === 'sr' ? 'Certifikati' : 'Certifications'}
            </h3>
            <div className="grid grid-cols-2 gap-3">
              {documents.filter(d => ['soc2', 'iso27001', 'hipaa', 'gdpr', 'certificate'].includes(d.document_type)).slice(0, 6).map(doc => (
                <div key={doc.id} className="p-3 bg-gray-50 dark:bg-gray-700/50 rounded-lg flex items-center gap-3">
                  <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${getTypeColor(doc.document_type)}`}>
                    <CheckCircle className="w-5 h-5" />
                  </div>
                  <div>
                    <div className="font-medium text-gray-900 dark:text-white text-sm">{documentTypes[doc.document_type as keyof typeof documentTypes]?.label}</div>
                    <div className="text-xs text-gray-500">{doc.download_count} {language === 'sr' ? 'preuzimanja' : 'downloads'}</div>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Recent Requests */}
          <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-5">
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4 flex items-center gap-2">
              <Clock className="w-5 h-5 text-orange-500" />
              {language === 'sr' ? 'Nedavni zahtjevi' : 'Recent Requests'}
            </h3>
            <div className="space-y-3">
              {pendingRequests.slice(0, 4).map(request => (
                <div key={request.id} className="p-3 bg-gray-50 dark:bg-gray-700/50 rounded-lg">
                  <div className="flex items-start justify-between">
                    <div>
                      <div className="font-medium text-gray-900 dark:text-white">{request.requester_name || request.requester_email}</div>
                      <div className="text-sm text-gray-500">{request.company_name}</div>
                      <div className="text-xs text-gray-400 mt-1">
                        {request.documents_requested.length} {language === 'sr' ? 'dokumenata zatraženo' : 'docs requested'}
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      <button 
                        onClick={() => handleApproveRequest(request.id)}
                        className="p-1.5 bg-green-100 text-green-700 rounded hover:bg-green-200"
                      >
                        <CheckCircle className="w-4 h-4" />
                      </button>
                      <button 
                        onClick={() => handleDenyRequest(request.id)}
                        className="p-1.5 bg-red-100 text-red-700 rounded hover:bg-red-200"
                      >
                        <AlertTriangle className="w-4 h-4" />
                      </button>
                    </div>
                  </div>
                </div>
              ))}
              {pendingRequests.length === 0 && (
                <p className="text-center py-4 text-gray-500 dark:text-gray-400">
                  {language === 'sr' ? 'Nema zahtjeva na čekanju' : 'No pending requests'}
                </p>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Documents Tab */}
      {activeTab === 'documents' && (
        <div className="space-y-4">
          <div className="flex items-center gap-4">
            <div className="flex-1 relative">
              <Search className="w-5 h-5 absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
              <input
                type="text"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder={language === 'sr' ? 'Pretraži dokumente...' : 'Search documents...'}
                className="w-full pl-10 pr-4 py-2 border border-gray-200 dark:border-gray-700 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-white"
              />
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {filteredDocuments.map(doc => (
              <div key={doc.id} className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-5">
                <div className="flex items-start justify-between mb-3">
                  <span className={`px-2 py-1 rounded text-xs ${getTypeColor(doc.document_type)}`}>
                    {documentTypes[doc.document_type as keyof typeof documentTypes]?.label}
                  </span>
                  <div className="flex items-center gap-1">
                    {doc.is_public ? (
                      <span title={language === 'sr' ? 'Javno' : 'Public'}>
                        <Globe className="w-4 h-4 text-green-500" />
                      </span>
                    ) : (
                      <span title={language === 'sr' ? 'Privatno' : 'Private'}>
                        <Lock className="w-4 h-4 text-gray-400" />
                      </span>
                    )}
                  </div>
                </div>
                
                <h4 className="font-semibold text-gray-900 dark:text-white mb-2">{doc.title}</h4>
                <p className="text-sm text-gray-500 dark:text-gray-400 line-clamp-2 mb-4">{doc.description}</p>
                
                <div className="flex items-center justify-between pt-3 border-t border-gray-200 dark:border-gray-700">
                  <span className="text-xs text-gray-500">
                    {doc.download_count} {language === 'sr' ? 'preuzimanja' : 'downloads'}
                  </span>
                  <div className="flex items-center gap-2">
                    <button className="p-1.5 text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-700 rounded">
                      <Eye className="w-4 h-4" />
                    </button>
                    <button className="p-1.5 text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-700 rounded">
                      <Settings className="w-4 h-4" />
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Requests Tab */}
      {activeTab === 'requests' && (
        <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 overflow-hidden">
          <table className="w-full">
            <thead className="bg-gray-50 dark:bg-gray-700/50">
              <tr>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">{language === 'sr' ? 'Zahtjevač' : 'Requester'}</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">{language === 'sr' ? 'Kompanija' : 'Company'}</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">{language === 'sr' ? 'Dokumenti' : 'Documents'}</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">NDA</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">{language === 'sr' ? 'Status' : 'Status'}</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">{language === 'sr' ? 'Akcije' : 'Actions'}</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
              {requests.map(request => (
                <tr key={request.id} className="hover:bg-gray-50 dark:hover:bg-gray-700/50">
                  <td className="px-4 py-3">
                    <div className="text-sm text-gray-900 dark:text-white">{request.requester_name || '-'}</div>
                    <div className="text-xs text-gray-500">{request.requester_email}</div>
                  </td>
                  <td className="px-4 py-3 text-sm text-gray-500">{request.company_name || '-'}</td>
                  <td className="px-4 py-3 text-sm text-gray-500">{request.documents_requested.length}</td>
                  <td className="px-4 py-3">
                    {request.nda_signed ? (
                      <CheckCircle className="w-5 h-5 text-green-500" />
                    ) : (
                      <Clock className="w-5 h-5 text-gray-400" />
                    )}
                  </td>
                  <td className="px-4 py-3">
                    <span className={`px-2 py-1 rounded text-xs ${
                      request.status === 'approved' ? 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-300' :
                      request.status === 'pending' ? 'bg-yellow-100 text-yellow-700 dark:bg-yellow-900/30 dark:text-yellow-300' :
                      request.status === 'denied' ? 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-300' :
                      'bg-gray-100 text-gray-500'
                    }`}>
                      {request.status}
                    </span>
                  </td>
                  <td className="px-4 py-3">
                    {request.status === 'pending' && (
                      <div className="flex items-center gap-2">
                        <button 
                          onClick={() => handleApproveRequest(request.id)}
                          className="p-1 bg-green-100 text-green-700 rounded hover:bg-green-200"
                        >
                          <CheckCircle className="w-4 h-4" />
                        </button>
                        <button 
                          onClick={() => handleDenyRequest(request.id)}
                          className="p-1 bg-red-100 text-red-700 rounded hover:bg-red-200"
                        >
                          <AlertTriangle className="w-4 h-4" />
                        </button>
                      </div>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          {requests.length === 0 && (
            <p className="text-center py-8 text-gray-500 dark:text-gray-400">
              {language === 'sr' ? 'Nema zahtjeva' : 'No requests'}
            </p>
          )}
        </div>
      )}

      {/* Settings Tab */}
      {activeTab === 'settings' && (
        <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-6 space-y-6">
          <h3 className="text-lg font-semibold text-gray-900 dark:text-white flex items-center gap-2">
            <Settings className="w-5 h-5 text-gray-500" />
            {language === 'sr' ? 'Postavke portala' : 'Portal Settings'}
          </h3>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {/* Custom Domain */}
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                {language === 'sr' ? 'Prilagođeni domen' : 'Custom Domain'}
              </label>
              <input
                type="text"
                value={settingsForm.custom_domain}
                onChange={(e) => setSettingsForm(prev => ({ ...prev, custom_domain: e.target.value }))}
                placeholder="trust.yourcompany.com"
                className="w-full border border-gray-300 dark:border-gray-600 rounded-lg px-3 py-2 bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
              />
              <p className="text-xs text-gray-500 mt-1">
                {language === 'sr' ? 'Ostavite prazno za podrazumevani domen' : 'Leave empty for default domain'}
              </p>
            </div>

            {/* Primary Color */}
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                {language === 'sr' ? 'Primarna boja' : 'Primary Color'}
              </label>
              <div className="flex items-center gap-3">
                <input
                  type="color"
                  value={settingsForm.primary_color}
                  onChange={(e) => setSettingsForm(prev => ({ ...prev, primary_color: e.target.value }))}
                  className="w-10 h-10 rounded border border-gray-300 cursor-pointer"
                />
                <input
                  type="text"
                  value={settingsForm.primary_color}
                  onChange={(e) => setSettingsForm(prev => ({ ...prev, primary_color: e.target.value }))}
                  className="flex-1 border border-gray-300 dark:border-gray-600 rounded-lg px-3 py-2 bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                />
              </div>
            </div>

            {/* Auto-approve domains */}
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                {language === 'sr' ? 'Automatski odobri domene' : 'Auto-approve Domains'}
              </label>
              <input
                type="text"
                value={settingsForm.auto_approve_domains}
                onChange={(e) => setSettingsForm(prev => ({ ...prev, auto_approve_domains: e.target.value }))}
                placeholder="partner.com, client.org"
                className="w-full border border-gray-300 dark:border-gray-600 rounded-lg px-3 py-2 bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
              />
              <p className="text-xs text-gray-500 mt-1">
                {language === 'sr' ? 'Domeni razdvojeni zarezom' : 'Comma-separated domains'}
              </p>
            </div>

            {/* Require NDA */}
            <div className="flex items-center gap-3 pt-6">
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="checkbox"
                  checked={settingsForm.require_nda}
                  onChange={(e) => setSettingsForm(prev => ({ ...prev, require_nda: e.target.checked }))}
                  className="w-4 h-4 text-teal-600 rounded"
                />
                <span className="text-sm text-gray-700 dark:text-gray-300">
                  {language === 'sr' ? 'Zahtijevaj NDA za pristup' : 'Require NDA for access'}
                </span>
              </label>
            </div>
          </div>

          {/* Visible Sections */}
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              {language === 'sr' ? 'Vidljive sekcije na portalu' : 'Visible Portal Sections'}
            </label>
            <div className="flex flex-wrap gap-3">
              {[
                { key: 'certifications', label: language === 'sr' ? 'Certifikati' : 'Certifications' },
                { key: 'policies', label: language === 'sr' ? 'Politike' : 'Policies' },
                { key: 'sub-processors', label: language === 'sr' ? 'Podprocesori' : 'Sub-processors' },
                { key: 'questionnaire', label: language === 'sr' ? 'Upitnik' : 'Questionnaire' },
                { key: 'pentest', label: language === 'sr' ? 'Pentest izvještaji' : 'Pentest Reports' },
                { key: 'compliance', label: language === 'sr' ? 'Usklađenost' : 'Compliance Status' },
              ].map(section => (
                <label key={section.key} className="flex items-center gap-2 px-3 py-2 bg-gray-50 dark:bg-gray-700/50 rounded-lg cursor-pointer">
                  <input
                    type="checkbox"
                    checked={settingsForm.visible_sections.includes(section.key)}
                    onChange={(e) => {
                      if (e.target.checked) {
                        setSettingsForm(prev => ({ ...prev, visible_sections: [...prev.visible_sections, section.key] }));
                      } else {
                        setSettingsForm(prev => ({ ...prev, visible_sections: prev.visible_sections.filter(s => s !== section.key) }));
                      }
                    }}
                    className="w-4 h-4 text-teal-600 rounded"
                  />
                  <span className="text-sm text-gray-700 dark:text-gray-300">{section.label}</span>
                </label>
              ))}
            </div>
          </div>

          {/* Save */}
          <div className="flex justify-end pt-4 border-t border-gray-200 dark:border-gray-700">
            <button
              onClick={handleSaveSettings}
              disabled={savingConfig}
              className="px-6 py-2 bg-teal-600 text-white rounded-lg hover:bg-teal-700 disabled:opacity-50 transition-colors"
            >
              {savingConfig 
                ? (language === 'sr' ? 'Čuvam...' : 'Saving...') 
                : (language === 'sr' ? 'Sačuvaj postavke' : 'Save Settings')
              }
            </button>
          </div>
        </div>
      )}

      {/* Import Policies Modal */}
      <Modal
        isOpen={showImportPoliciesModal}
        onClose={() => setShowImportPoliciesModal(false)}
        title={language === 'sr' ? 'Uvezi sa politika' : 'Import from Policies'}
        footer={
          <div className="flex justify-end gap-3">
            <button
              onClick={() => setShowImportPoliciesModal(false)}
              className="px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700"
            >
              {language === 'sr' ? 'Otkaži' : 'Cancel'}
            </button>
            <button
              onClick={handleImportPolicies}
              disabled={importing || selectedPolicies.length === 0}
              className="px-4 py-2 bg-teal-600 text-white rounded-lg hover:bg-teal-700 disabled:opacity-50"
            >
              {importing ? (language === 'sr' ? 'Uvozim...' : 'Importing...') : (language === 'sr' ? 'Uvezi odabrane' : 'Import Selected')}
            </button>
          </div>
        }
      >
        <div className="space-y-4">
          <p className="text-sm text-gray-600 dark:text-gray-400">
            {language === 'sr' 
              ? 'Odaberite politike koje želite da uvezete u Trust Center za deljenje sa klijentima i partnerima.'
              : 'Select policies you want to import into Trust Center for sharing with clients and partners.'}
          </p>
          
          {policies.length === 0 ? (
            <p className="text-gray-500 text-center py-4">
              {language === 'sr' ? 'Nema dostupnih politika' : 'No policies available'}
            </p>
          ) : (
            <div className="max-h-64 overflow-y-auto border border-gray-200 dark:border-gray-700 rounded-lg divide-y divide-gray-200 dark:divide-gray-700">
              {policies.map((policy: any) => (
                <label 
                  key={policy.id} 
                  className="flex items-center gap-3 p-3 hover:bg-gray-50 dark:hover:bg-gray-700 cursor-pointer"
                >
                  <input
                    type="checkbox"
                    checked={selectedPolicies.includes(policy.id)}
                    onChange={(e) => {
                      if (e.target.checked) {
                        setSelectedPolicies([...selectedPolicies, policy.id]);
                      } else {
                        setSelectedPolicies(selectedPolicies.filter(id => id !== policy.id));
                      }
                    }}
                    className="w-4 h-4 text-teal-600 rounded"
                  />
                  <div className="flex-1">
                    <p className="text-sm font-medium text-gray-900 dark:text-white">
                      {policy.title || policy.name}
                    </p>
                    <p className="text-xs text-gray-500">
                      {policy.framework || policy.category || (language === 'sr' ? 'Opšta politika' : 'General Policy')}
                    </p>
                  </div>
                  <span className={`px-2 py-1 text-xs rounded ${
                    policy.status === 'approved' ? 'bg-green-100 text-green-700' : 'bg-yellow-100 text-yellow-700'
                  }`}>
                    {policy.status || 'draft'}
                  </span>
                </label>
              ))}
            </div>
          )}
          
          {selectedPolicies.length > 0 && (
            <p className="text-sm text-teal-600">
              {language === 'sr' 
                ? `${selectedPolicies.length} politika odabrano` 
                : `${selectedPolicies.length} policies selected`}
            </p>
          )}
        </div>
      </Modal>

      {/* Add Document Modal */}
      <Modal
        isOpen={showAddDocumentModal}
        onClose={() => setShowAddDocumentModal(false)}
        title={language === 'sr' ? 'Dodaj dokument' : 'Add Document'}
        footer={
          <div className="flex justify-end gap-3">
            <button
              onClick={() => setShowAddDocumentModal(false)}
              className="px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700"
            >
              {language === 'sr' ? 'Otkaži' : 'Cancel'}
            </button>
            <button
              className="px-4 py-2 bg-teal-600 text-white rounded-lg hover:bg-teal-700"
              onClick={() => {
                // TODO: Implement add new document
                setShowAddDocumentModal(false);
              }}
            >
              {language === 'sr' ? 'Dodaj' : 'Add'}
            </button>
          </div>
        }
      >
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              {language === 'sr' ? 'Tip dokumenta' : 'Document Type'}
            </label>
            <select className="w-full border border-gray-300 dark:border-gray-600 rounded-lg px-3 py-2 bg-white dark:bg-gray-700 text-gray-900 dark:text-white">
              {Object.entries(documentTypes).map(([key, { label }]) => (
                <option key={key} value={key}>
                  {label}
                </option>
              ))}
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              {language === 'sr' ? 'Naslov' : 'Title'}
            </label>
            <input
              type="text"
              className="w-full border border-gray-300 dark:border-gray-600 rounded-lg px-3 py-2 bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
              placeholder={language === 'sr' ? 'Unesite naslov dokumenta' : 'Enter document title'}
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              {language === 'sr' ? 'Opis' : 'Description'}
            </label>
            <textarea
              className="w-full border border-gray-300 dark:border-gray-600 rounded-lg px-3 py-2 bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
              rows={3}
              placeholder={language === 'sr' ? 'Opis dokumenta' : 'Document description'}
            />
          </div>
          <div className="flex items-center gap-6">
            <label className="flex items-center gap-2">
              <input type="checkbox" className="w-4 h-4 text-teal-600 rounded" />
              <span className="text-sm text-gray-700 dark:text-gray-300">
                {language === 'sr' ? 'Javno dostupno' : 'Publicly available'}
              </span>
            </label>
            <label className="flex items-center gap-2">
              <input type="checkbox" className="w-4 h-4 text-teal-600 rounded" />
              <span className="text-sm text-gray-700 dark:text-gray-300">
                {language === 'sr' ? 'Zahtijeva NDA' : 'Requires NDA'}
              </span>
            </label>
          </div>
        </div>
      </Modal>
    </div>
  );
};

export default EnterpriseTrustCenter;
